import type React from "react";
import { cn } from "../../lib/utils";
import type { Omittable } from "../../lib/props";

export type SkeletonProps = React.ComponentProps<"div"> &
  Omittable<{
  /** Bar width. A number is pixels; a string is any CSS length. */
  width?: number | string;
  /** Bar height. A number is pixels; a string is any CSS length. */
  height?: number | string;
  /** Fills the inline axis, for a bar that spans its container. */
  fillWidth?: boolean;
  /** Circular, for an avatar or icon placeholder. Pair with `width`. */
  circle?: boolean;
}>;

export function Skeleton({
  className,
  width,
  height,
  fillWidth = false,
  circle = false,
  style,
  ...props
}: SkeletonProps): React.ReactElement {
  // A skeleton is defined by its dimensions, so they are the props. Every other
  // primitive here takes sizing the same way (see `Box width`/`height`), and a
  // placeholder is the one component where a bare className is almost always
  // just a width.
  const sizing: React.CSSProperties = {
    ...(width != null && { width }),
    ...(height != null && { height }),
    ...(circle && width != null && { height: width }),
  };

  return (
    <div
      className={cn(
        "animate-skeleton rounded-sm [--skeleton-highlight:--alpha(var(--color-white)/64%)] [background:linear-gradient(120deg,transparent_40%,var(--skeleton-highlight),transparent_60%)_var(--color-muted)_0_0/200%_100%_fixed] dark:[--skeleton-highlight:--alpha(var(--color-white)/4%)]",
        fillWidth && "w-full",
        circle && "rounded-full",
        className,
      )}
      data-slot="skeleton"
      style={Object.keys(sizing).length ? { ...sizing, ...style } : style}
      {...props}
    />
  );
}
